import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Zap, X, FlaskConical } from "lucide-react";
import api from "../lib/api";

// EnvBanner — admin-only "Production Mode" banner.
// Pulls /admin/integrations/environment and renders a single-line warning
// strip when ANY integration is in test/sandbox mode, or when the overall
// mode is "mixed". Stays out of the way once everything is live.
//
// Dismissal is per-tab session (sessionStorage). Re-appears on next admin login.
export default function EnvBanner() {
  const [env, setEnv] = useState(null);
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && sessionStorage.getItem("env-banner-dismissed") === "1"
  );

  useEffect(() => {
    let alive = true;
    api.get("/admin/integrations/environment")
      .then(r => { if (alive) setEnv(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  if (!env || dismissed) return null;
  if (env.overall === "live") return null; // fully live → silent

  const tone = env.overall === "mixed"
    ? { bg: "bg-amber-100/90", border: "border-amber-300", text: "text-amber-900", Icon: AlertTriangle, label: "Mixed mode" }
    : env.overall === "test"
    ? { bg: "bg-blue-100/90", border: "border-blue-300", text: "text-blue-900", Icon: FlaskConical, label: "Test mode" }
    : { bg: "bg-secondary", border: "border-border", text: "text-foreground/70", Icon: Zap, label: "Not configured" };

  const testKeys = Object.entries(env.integrations)
    .filter(([, v]) => v.mode === "test")
    .map(([, v]) => v.label.split(" · ")[0]);

  const liveKeys = Object.entries(env.integrations)
    .filter(([, v]) => v.mode === "live")
    .map(([, v]) => v.label.split(" · ")[0]);

  const message =
    env.overall === "test"
      ? `All money & shipping providers are running on TEST keys (${testKeys.join(" · ")}). No real charges or shipments will occur.`
      : env.overall === "mixed"
      ? `Heads up — ${testKeys.length} provider${testKeys.length === 1 ? "" : "s"} on TEST (${testKeys.join(", ")}) while ${liveKeys.length} ${liveKeys.length === 1 ? "is" : "are"} LIVE (${liveKeys.join(", ")}). Avoid demos here.`
      : "No payment or shipping integrations are configured yet.";

  return (
    <div
      data-testid="env-banner"
      className={`${tone.bg} ${tone.border} ${tone.text} border-b px-6 py-2 flex items-center gap-3 text-[12.5px]`}
    >
      <tone.Icon className="w-4 h-4 shrink-0" />
      <span className="font-semibold mono uppercase tracking-widest text-[10.5px]">
        {tone.label}
      </span>
      <span className="flex-1 truncate">{message}</span>
      <Link
        to="/admin/integrations"
        data-testid="env-banner-cta"
        className="hidden sm:inline-flex items-center gap-1 text-[11px] underline underline-offset-2 hover:no-underline"
      >
        View integrations
      </Link>
      <button
        type="button"
        onClick={() => { sessionStorage.setItem("env-banner-dismissed", "1"); setDismissed(true); }}
        data-testid="env-banner-dismiss"
        className="opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss banner"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
